import React, {useEffect, useState} from "react";
import {Helmet} from "react-helmet";
import useSquadflixSearchData from "../../hooks/useSquadflixSearchData";
import UseSquadflixNavigate from "../../squadflixHome/useSquadflixNavigate";
import {getYearOfDate} from "../../../../utils/utils";
import {Spinner} from "../../../shared";
import TvMediaCard from "../components/TvMediaCard";

const KEYBOARD_KEYS = [
    "A", "B", "C", "D", "E", "F",
    "G", "H", "I", "J", "K", "L",
    "M", "N", "Ñ", "O", "P", "Q",
    "R", "S", "T", "U", "V", "W",
    "X", "Y", "Z", "1", "2", "3",
    "4", "5", "6", "7", "8", "9",
    "0"
];

const SquadflixTvSearch: React.FC = () => {
    const {
        nameSearch,
        setNameSearch,
        currentSearch,
        loading,
        searchHistory,
        clearHistory,
        makeSearch
    } = useSquadflixSearchData({autoLoadLastSearch: true});

    const squadflixNav = UseSquadflixNavigate({});
    const [filterType, setFilterType] = useState<"all" | "movies" | "series">("all");

    // Auto-search with debounce when nameSearch changes
    useEffect(() => {
        const trimmed = (nameSearch || "").trim();
        if (trimmed.length < 2) return;
        const timer = setTimeout(() => {
            makeSearch(trimmed, false);
        }, 420);
        return () => clearTimeout(timer);
    }, [nameSearch, makeSearch]);

    const handleKeyPress = (char: string) => {
        setNameSearch((nameSearch || "") + char.toLowerCase());
    };

    const handleSpace = () => {
        setNameSearch((nameSearch || "") + " ");
    };

    const handleBackspace = () => {
        setNameSearch((nameSearch || "").slice(0, -1));
    };

    const handleClear = () => {
        setNameSearch("");
    };

    const movies = currentSearch?.movies || [];
    const series = currentSearch?.series || [];

    return (
        <div className="tv-search-screen">
            <Helmet>
                <meta charSet="utf-8" />
                <title>Buscar | SquadFlix TV</title>
            </Helmet>

            {/* Left Column: On-Screen TV Keyboard & Search History */}
            <aside className="tv-search-screen__left">
                {/* Input box for voice remote or physical keyboard */}
                <div className="tv-search-screen__input-box" data-tv-row="tv-search-input">
                    <i className="fa-solid fa-magnifying-glass tv-search-screen__input-icon"></i>
                    <input
                        type="text"
                        data-tv-focusable="true"
                        value={nameSearch || ""}
                        onChange={(e) => setNameSearch(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                makeSearch(nameSearch, true);
                            }
                        }}
                        placeholder="Busca películas o series..."
                        className="tv-search-screen__input"
                    />
                    {!!nameSearch && (
                        <button
                            type="button"
                            data-tv-focusable="true"
                            className="tv-btn tv-btn--ghost tv-btn--sm"
                            onClick={handleClear}
                            title="Vaciar">
                            <i className="fa-solid fa-xmark"></i>
                        </button>
                    )}
                </div>

                {/* Quick Action Keys (Space, Backspace, Search) */}
                <div className="tv-search-screen__key-actions" data-tv-row="tv-search-actions">
                    <button
                        type="button"
                        data-tv-focusable="true"
                        data-tv-autofocus="true"
                        className="tv-key-btn tv-key-btn--wide"
                        onClick={handleSpace}>
                        <i className="fa-solid fa-arrows-left-right-to-line pe-2"></i>ESPACIO
                    </button>
                    <button
                        type="button"
                        data-tv-focusable="true"
                        className="tv-key-btn tv-key-btn--wide"
                        onClick={handleBackspace}>
                        <i className="fa-solid fa-delete-left pe-2"></i>BORRAR
                    </button>
                    <button
                        type="button"
                        data-tv-focusable="true"
                        className="tv-key-btn tv-key-btn--accent"
                        onClick={() => makeSearch(nameSearch, true)}>
                        <i className="fa-solid fa-magnifying-glass pe-2"></i>BUSCAR
                    </button>
                </div>

                {/* Alphanumeric Keyboard Grid */}
                <div className="tv-search-screen__keyboard" data-tv-row="tv-search-keyboard" data-tv-grid="true">
                    {KEYBOARD_KEYS.map((k) => (
                        <button
                            key={k}
                            type="button"
                            data-tv-focusable="true"
                            className="tv-key-btn"
                            onClick={() => handleKeyPress(k)}>
                            {k}
                        </button>
                    ))}
                </div>

                {/* Recent Search History */}
                {searchHistory.length > 0 && (
                    <div className="tv-search-screen__history" data-tv-row="tv-search-history">
                        <div className="tv-search-screen__history-header">
                            <span>
                                <i className="fa-solid fa-clock-rotate-left pe-2"></i>Búsquedas recientes
                            </span>
                            <button
                                type="button"
                                data-tv-focusable="true"
                                className="tv-btn tv-btn--ghost tv-btn--sm"
                                onClick={clearHistory}>
                                Borrar
                            </button>
                        </div>
                        <div className="tv-search-screen__history-chips">
                            {searchHistory.slice(0, 8).map((term) => (
                                <button
                                    key={term}
                                    type="button"
                                    data-tv-focusable="true"
                                    className="tv-pill-btn"
                                    onClick={() => {
                                        setNameSearch(term);
                                        makeSearch(term, true);
                                    }}>
                                    {term}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </aside>

            {/* Right Column: Instant Results */}
            <main className="tv-search-screen__right">
                <div className="tv-search-screen__results-header" data-tv-row="tv-search-filters">
                    <div>
                        <h2 className="tv-search-screen__results-title">
                            {currentSearch?.keywords
                                ? `Resultados para "${currentSearch.keywords}"`
                                : "Explora el catálogo"}
                        </h2>
                    </div>

                    <div className="tv-search-screen__filters">
                        <button
                            type="button"
                            data-tv-focusable="true"
                            className={`tv-pill-btn ${filterType === "all" ? "is-active" : ""}`}
                            onClick={() => setFilterType("all")}>
                            Todo ({movies.length + series.length})
                        </button>
                        <button
                            type="button"
                            data-tv-focusable="true"
                            className={`tv-pill-btn ${filterType === "movies" ? "is-active" : ""}`}
                            onClick={() => setFilterType("movies")}>
                            Películas ({movies.length})
                        </button>
                        <button
                            type="button"
                            data-tv-focusable="true"
                            className={`tv-pill-btn ${filterType === "series" ? "is-active" : ""}`}
                            onClick={() => setFilterType("series")}>
                            Series ({series.length})
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="tv-loading-center py-5">
                        <Spinner />
                    </div>
                ) : (
                    <div className="tv-search-screen__results-content">
                        {(filterType === "all" || filterType === "movies") && movies.length > 0 && (
                            <section className="tv-search-screen__section">
                                <h3 className="tv-search-screen__section-title">
                                    <i className="fa-solid fa-film pe-2 text-danger"></i>Películas ({movies.length})
                                </h3>
                                <div className="tv-search-screen__grid" data-tv-row="tv-search-movies-grid" data-tv-grid="true">
                                    {movies.map((m: any) => (
                                        <TvMediaCard
                                            key={`movie_${m.id}`}
                                            width="100%"
                                            aspectRatio="2/3"
                                            tmdbSrc={m.poster_path || m.backdrop_path}
                                            badgeTopLeft={m.view ? "Vista" : undefined}
                                            badgeTopRight={
                                                m.vote_average ? (
                                                    <>
                                                        {m.vote_average.toFixed(1)}{" "}
                                                        <i className="fa-solid fa-star text-warning"></i>
                                                    </>
                                                ) : undefined
                                            }
                                            meta={getYearOfDate(m.release_date)}
                                            title={m.title || m.name}
                                            onClick={() => {
                                                if (nameSearch) makeSearch(nameSearch, true);
                                                squadflixNav.navigateToMovie(m);
                                            }}
                                        />
                                    ))}
                                </div>
                            </section>
                        )}

                        {(filterType === "all" || filterType === "series") && series.length > 0 && (
                            <section className="tv-search-screen__section">
                                <h3 className="tv-search-screen__section-title">
                                    <i className="fa-solid fa-tv pe-2 text-danger"></i>Series ({series.length})
                                </h3>
                                <div className="tv-search-screen__grid" data-tv-row="tv-search-series-grid" data-tv-grid="true">
                                    {series.map((s: any) => (
                                        <TvMediaCard
                                            key={`serie_${s.id}`}
                                            width="100%"
                                            aspectRatio="2/3"
                                            tmdbSrc={s.poster_path || s.backdrop_path}
                                            badgeTopRight={
                                                s.vote_average ? (
                                                    <>
                                                        {s.vote_average.toFixed(1)}{" "}
                                                        <i className="fa-solid fa-star text-warning"></i>
                                                    </>
                                                ) : undefined
                                            }
                                            meta={getYearOfDate(s.first_air_date)}
                                            title={s.name || s.title}
                                            onClick={() => {
                                                if (nameSearch) makeSearch(nameSearch, true);
                                                squadflixNav.navigateToSerie(s);
                                            }}
                                        />
                                    ))}
                                </div>
                            </section>
                        )}

                        {movies.length === 0 && series.length === 0 && (
                            <div className="tv-empty-state">
                                <i className="fa-solid fa-magnifying-glass fa-3x mb-3 opacity-50"></i>
                                <h4>Usa el teclado con el mando para buscar películas y series</h4>
                                <p className="text-muted">
                                    Los resultados aparecerán aquí en tiempo real mientras escribes.
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
};

export default SquadflixTvSearch;
